import React, { useState, useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Confetti from 'react-confetti';
import { motion, AnimatePresence } from 'framer-motion';
import {
  getOnboardingQuestions,
  submitOnboardingAnswers,
  clearOnboarding
} from '../actions/onboardingActions';
import { loadUser } from '../actions/authActions';

const OnboardingPage = ({
  onboarding,
  auth,
  getOnboardingQuestions,
  submitOnboardingAnswers,
  clearOnboarding,
  loadUser
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [assessmentCompleted, setAssessmentCompleted] = useState(false); // Add this flag
  const [windowDimension, setWindowDimension] = useState({ width: 300, height: 300 });
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    // Set window dimensions for confetti
    setWindowDimension({ width: window.innerWidth, height: window.innerHeight });
  }, []);

  useEffect(() => {
    // Don't run useEffect if we're navigating or assessment is completed
    if (isNavigating || assessmentCompleted) {
      return;
    }

    if (!auth.isAuthenticated) {
      navigate('/');
      return;
    }

    // Only students should access onboarding
    if (auth.user && auth.user.role !== 'student') {
      navigate('/dashboard');
      return;
    }

    // Don't load questions if we already have results or are submitting
    if (onboarding.result || showResult || isSubmitting) {
      return;
    }

    // Only redirect if user has completed onboarding AND we're not in the middle of completing assessment
    // AND we're not already navigating to prevent loops
    if (auth.user && auth.user.hasCompletedOnboarding && !assessmentCompleted && !isNavigating) {
      setIsNavigating(true); // Prevent further navigation attempts
      navigate('/dashboard');
      return;
    }

    getOnboardingQuestions();

    return () => {
      clearOnboarding();
    };
  }, [auth.isAuthenticated, auth.user, onboarding.result, showResult, isSubmitting, isNavigating, assessmentCompleted, getOnboardingQuestions, clearOnboarding, navigate]);

  useEffect(() => {
    if (onboarding.result) {
      // Immediate User State Update
      if (onboarding.result.user) {
        dispatch({ type: 'USER_LOADED', payload: onboarding.result.user });
      } else {
        loadUser();
      }

      // Conditional Result Display with Fallback Mechanism
      const checkAndShowResult = () => {
        if (auth.user && auth.user.hasCompletedOnboarding) {
          setShowResult(true);
          setIsSubmitting(false);
          setAssessmentCompleted(true);
          setShowConfetti(true);

          // Stop confetti after 4 seconds using requestAnimationFrame
          const startTime = performance.now();
          const stopConfetti = () => {
            const elapsed = performance.now() - startTime;
            if (elapsed < 4000) {
              requestAnimationFrame(stopConfetti);
            } else {
              setShowConfetti(false);
            }
          };
          requestAnimationFrame(stopConfetti);
        } else {
          // Fallback: wait 500ms and check again
          setTimeout(checkAndShowResult, 500);
        }
      };

      checkAndShowResult();
    }
  }, [onboarding.result, auth.user, loadUser, dispatch]);

  // Cleanup effect to reset navigation state
  useEffect(() => {
    return () => {
      setIsNavigating(false);
    };
  }, []);

  const handleAnswerSelect = (optionIndex) => {
    const currentQuestion = onboarding.questions[currentQuestionIndex];
    
    // Update answers array
    const newAnswers = [...answers];
    const existingAnswerIndex = newAnswers.findIndex(
      answer => answer.questionId === currentQuestion._id
    );

    if (existingAnswerIndex !== -1) {
      newAnswers[existingAnswerIndex] = {
        questionId: currentQuestion._id,
        optionIndex
      };
    } else {
      newAnswers.push({
        questionId: currentQuestion._id,
        optionIndex
      });
    }

    setAnswers(newAnswers);
  };

  const handleNext = () => {
    if (currentQuestionIndex < onboarding.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleSubmit = () => {
    if (answers.length === onboarding.questions.length) {
      setIsSubmitting(true);
      submitOnboardingAnswers(answers);
    }
  };

  const handleContinue = async () => {
    if (isNavigating) return;
    setIsNavigating(true);

    try {
      // First clear onboarding state to prevent loops
      clearOnboarding();
      
      // Refresh user data to ensure hasCompletedOnboarding is updated
      await loadUser();
      
      // Wait a bit more for the data to propagate in slow environments
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Navigate to dashboard
      navigate('/dashboard');
    } catch (error) {
      console.error('Error refreshing user data:', error);
      // Still navigate even if refresh fails
      navigate('/dashboard');
    }
  };

  // Show loading state for initial load only, not for submission
  if (onboarding.loading && !isSubmitting && !showResult) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 pt-20 pb-20 lg:pb-4">
        <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-xl border border-white/30 p-6 sm:p-8">
          <div className="text-[#1e2d40] text-lg sm:text-xl text-center">Loading assessment...</div>
        </div>
      </div>
    );
  }

  // Show submission loading state
  if (isSubmitting && onboarding.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 pt-20 pb-20 lg:pb-4">
        <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-xl border border-white/30 p-6 sm:p-8 text-center max-w-md mx-auto">
          <div className="text-[#1e2d40] text-lg sm:text-xl mb-4">Submitting your assessment...</div>
          <div className="text-gray-600 text-sm">Please wait while we process your answers</div>
        </div>
      </div>
    );
  }

  // Show error if there are any API errors (but not during submission)
  if (!onboarding.loading && !onboarding.questions.length && !showResult && !isSubmitting) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 pt-20 pb-20 lg:pb-4">
        <div className="max-w-md mx-auto bg-white/80 backdrop-blur-sm rounded-xl shadow-xl border border-white/30 p-6 sm:p-8 text-center">
          <div className="mb-6">
            <svg className="w-12 h-12 sm:w-16 sm:h-16 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <h2 className="text-xl sm:text-2xl font-bold text-red-500 mb-2">Assessment Unavailable</h2>
            <p className="text-[#1e2d40] mb-4 text-sm sm:text-base">
              Unable to load your assessment questions. This could be because:
            </p>
            <ul className="text-left text-gray-600 text-sm space-y-1 mb-6">
              <li>• You are not assigned to a group</li>
              <li>• Your group doesn't have a program</li>
              <li>• No questions are available for your program</li>
            </ul>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-[#1086a4] hover:bg-[#054581] text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 w-full sm:w-auto"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (showResult && onboarding.result) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 pt-20 pb-20 lg:pb-4 overflow-hidden">
        {showConfetti && (
          <Confetti
            width={windowDimension.width}
            height={windowDimension.height}
            gravity={0.3}
            numberOfPieces={200}
            recycle={false}
          />
        )}
        
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl mx-auto bg-white/80 backdrop-blur-sm rounded-xl shadow-xl border border-white/30 p-6 sm:p-8"
        >
          <div className="text-center">
            <div className="mb-6">
              <svg className="w-12 h-12 sm:w-16 sm:h-16 text-green-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h2 className="text-2xl sm:text-3xl font-bold text-green-500 mb-2">
                Congratulations!
              </h2>
              <p className="text-[#1e2d40] text-sm sm:text-base">
                <span className="font-bold">welcome to </span>
                <span className="font-bold text-[#1086a4]">
                  {onboarding.result.programTitle || 'the program'}
                </span>
              </p>
            </div>

            <div className="bg-gray-50 border border-gray-200 p-4 sm:p-6 rounded-lg mb-6">
              <div className="text-center">
                <h3 className="text-lg sm:text-xl font-bold mb-4 text-[#1086a4]">Your Results</h3>
                <p className="text-base sm:text-lg text-[#1e2d40] font-bold mb-2">
                  Based on your answers your pathway is: <span className="text-[#1086a4]">{onboarding.result.pathTitle}</span>
                </p>
                <br />
                {onboarding.result.assignedPath?.description && (
                  <p className="text-sm sm:text-base text-[#1086a4] font-bold">
                    {onboarding.result.assignedPath.description}
                  </p>
                )}
              </div>
            </div>

            <div className="bg-gray-50 border border-gray-200 p-4 sm:p-6 rounded-lg mb-6">
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  className="absolute top-0 left-0 w-full h-full rounded-lg"
                  src="https://www.youtube.com/embed/Frb72eDy5fs"
                  title="Welcome Video"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>

            <button
              onClick={handleContinue}
              disabled={isNavigating}
              className={`w-full font-semibold py-3 px-6 rounded-lg transition-colors duration-200 ${
                isNavigating
                  ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                  : 'bg-[#1086a4] hover:bg-[#054581] text-white'
              }`}
            >
              {isNavigating ? 'Updating your profile...' : 'Continue to Dashboard'}
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  // Don't show questions if we have results or are submitting
  if (showResult || isSubmitting) {
    return null; // This will be handled by the result display logic above
  }

  if (!onboarding.questions.length) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 pt-20 pb-20 lg:pb-4">
        <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-xl border border-white/30 p-6 sm:p-8 text-center max-w-md mx-auto">
          <h2 className="text-xl sm:text-2xl font-bold text-[#1e2d40] mb-4">No Assessment Available</h2>
          <p className="text-gray-600 mb-6 text-sm sm:text-base">There are no questions available for your program.</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-[#1086a4] hover:bg-[#054581] text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 w-full sm:w-auto"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const currentQuestion = onboarding.questions[currentQuestionIndex];
  const currentAnswer = answers.find(answer => answer.questionId === currentQuestion._id);
  const progress = ((currentQuestionIndex + 1) / onboarding.questions.length) * 100;
  const isLastQuestion = currentQuestionIndex === onboarding.questions.length - 1;
  const canProceed = currentAnswer !== undefined;
  const allQuestionsAnswered = answers.length === onboarding.questions.length;

  return (
    <div className="min-h-screen overflow-auto pt-16 pb-16 lg:pb-0">
      <div className="max-w-3xl mx-auto px-4 py-6 sm:py-8 min-h-screen">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold mb-2 text-[#054581]">Find your path</h1>
          <p className="text-gray-600 mb-4 text-sm sm:text-base">
            Program: <span className="text-[#1086a4] font-semibold">{onboarding.programTitle}</span>
          </p>
          
          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
            <motion.div
              className="bg-gradient-to-r from-[#1086a4] to-[#054581] h-3 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            />
          </div>
          <p className="text-xs sm:text-sm text-gray-600">
            Question {currentQuestionIndex + 1} of {onboarding.questions.length}
          </p>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestionIndex}
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ duration: 0.5 }}
            className="bg-white/80 backdrop-blur-sm rounded-xl shadow-xl border border-white/30 p-6 sm:p-8 mb-6 sm:mb-8"
          >
            <h2 className="text-lg sm:text-xl font-semibold mb-6 text-[#1e2d40]">{currentQuestion.text}</h2>
            
            <div className="space-y-3">
              {currentQuestion.options.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleAnswerSelect(index)}
                  className={`w-full text-left p-4 rounded-lg border-2 transition-colors duration-200 ${
                    currentAnswer?.optionIndex === index
                      ? 'border-[#1086a4] bg-[#1086a4] bg-opacity-10 text-[#1086a4]'
                      : 'border-gray-300 bg-white hover:border-[#1086a4] hover:bg-gray-50 text-[#1e2d40]'
                  }`}
                >
                  <div className="flex items-center">
                    <span className="w-6 h-6 rounded-full border-2 border-current mr-4 flex items-center justify-center flex-shrink-0">
                      {currentAnswer?.optionIndex === index && (
                        <span className="w-3 h-3 rounded-full bg-current"></span>
                      )}
                    </span>
                    <span className="text-sm sm:text-base">{option.text}</span>
                  </div>
                </button>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          <button
            onClick={handlePrevious}
            disabled={currentQuestionIndex === 0}
            className={`w-full sm:w-auto px-6 py-3 rounded-lg font-semibold transition-colors duration-200 ${
              currentQuestionIndex === 0
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-gray-600 text-white hover:bg-gray-700'
            }`}
          >
            Previous
          </button>

          <div className="text-center order-first sm:order-none">
            <p className="text-xs sm:text-sm text-gray-600">
              {answers.length} of {onboarding.questions.length} questions answered
            </p>
          </div>

          {isLastQuestion ? (
            <button
              onClick={handleSubmit}
              disabled={!allQuestionsAnswered}
              className={`w-full sm:w-auto px-6 py-3 rounded-lg font-semibold transition-colors duration-200 ${
                allQuestionsAnswered
                  ? 'bg-[#1086a4] text-white hover:bg-[#054581]'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              Submit Assessment
            </button>
          ) : (
            <button
              onClick={handleNext}
              disabled={!canProceed}
              className={`w-full sm:w-auto px-6 py-3 rounded-lg font-semibold transition-colors duration-200 ${
                canProceed
                  ? 'bg-[#1086a4] text-white hover:bg-[#054581]'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              Next
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  onboarding: state.onboarding,
  auth: state.auth
});

export default connect(mapStateToProps, {
  getOnboardingQuestions,
  submitOnboardingAnswers,
  clearOnboarding,
  loadUser
})(OnboardingPage);
